// The prequalification form's PERSISTENCE SEAM.
//
// Decision 18 says the broker's form IS the client's form: one codebase, two
// contexts. But the two contexts authenticate completely differently — the
// anonymous funnel presents a bearer token in a query string, while the
// assisted surface is session-authenticated and addresses the row by id.
//
// So the form component takes an ADAPTER AS A PROP. It never receives a token
// and it never receives a draft id.
//
// That is not stylistic. If the form reached for the token itself there would
// be exactly two ways to reuse it from the assisted surface: hand that surface
// a visitor's token (forbidden — a token surfaced into a staff or portal UI
// turns a screen-share, a screenshot or a support ticket into a permanent
// credential leak), or fork the component (which breaks decision 18 on the
// first divergent bug fix). The adapter is what makes neither necessary: the
// form says "persist this step" and the implementation decides what that means.
//
// THREE OPERATIONS, and create is deliberately not one of them. Whether a write
// needs to mint a row first is an implementation detail of the anonymous
// adapter — the assisted adapter is always handed a draft that already exists.
// A fourth `create` method would force the assisted side to implement something
// it must never do, and the natural stub for "must never do" is a silent no-op.

import type { ReactNode } from "react";
import type {
  PrequalBuyerAnswers,
  PrequalFinalizeRequest,
  PrequalFinalizeResult,
  PrequalRole,
  PrequalSellerAnswers,
} from "../../api/prequalification";

/**
 * Which context the form is rendering in.
 *
 * Carried on the adapter rather than passed as a second prop, and never
 * inferred from the route: the assisted surface needs it to SUPPRESS the
 * decision-22 resume copy, and that copy promises the applicant a link back
 * into their application. In assisted mode the invite behind the draft is
 * minted with its email suppressed on purpose — the client is in the room —
 * so rendering the promise there would promise something nobody will send.
 */
export type PrequalPersistenceMode = "anonymous" | "assisted";

/** The role's answers payload — exactly the shape the server's strict reader
 *  accepts. An extra member is a 400, so this is a union of the two DTOs and
 *  never a looser record type. */
export type PrequalAnswers = PrequalBuyerAnswers | PrequalSellerAnswers;

/**
 * Who the application is for.
 *
 * Not answer content — identity. It is what makes a follow-up list actionable,
 * and on the organic path it is also the reason a draft can exist at all: the
 * server will not create one without a name and a valid email.
 */
export interface PrequalDraftIdentity {
  firstName: string;
  lastName: string;
  email: string;
}

/** Everything a write carries. No token, no id, no row version — the adapter
 *  owns all three. */
export interface PrequalDraftSnapshot {
  /** The step the visitor is moving TO. Clamped server-side to the funnel's own
   *  range, so a bad value can never render "step 400 of 6". */
  stepIndex: number;
  answers: PrequalAnswers;
  identity: PrequalDraftIdentity;
}

/** What a resumed draft gives the form back. */
export interface PrequalDraftLoad {
  role: PrequalRole;
  stepIndex: number;
  /**
   * The server's own denominator for this funnel.
   *
   * Pinned against the funnel's step array by a drift test per role. Nothing
   * imports across the language boundary, so the two numbers can only be kept
   * honest by asserting them against each other: a step added on this side
   * without the matching server constant would report "5 of 6" to every client
   * of that funnel forever, including the ones who finished.
   */
  stepCount: number;
  answers: PrequalAnswers;
  identity: PrequalDraftIdentity;
  partnerSlug: string | null;
}

/**
 * How a write ended.
 *
 * `skipped` is a first-class outcome, not a failure: on the organic path there
 * is nothing to save until the visitor has told us who they are, and reporting
 * that as an error would put a red notice on a funnel that is working exactly
 * as intended.
 */
export type PrequalDraftWriteStatus = "saved" | "skipped" | "conflict" | "failed";

export interface PrequalDraftWriteResult {
  status: PrequalDraftWriteStatus;
  /**
   * Populated on the ORGANIC create and never again — this is the one moment
   * the resume token is emitted to anyone. There is no email fallback by
   * design, so a caller that receives this and does not render it has stranded
   * the visitor.
   *
   * Always the URL the server built, verbatim. The query parameter name is
   * what selects the store on the way back in, so assembling it client-side is
   * a mistake that surfaces only when a real person's link fails.
   */
  resumeUrl?: string | null;
  /** A message the form may render as-is. Never raw HTTP text. */
  message?: string | null;
}

/**
 * The seam. Implemented by `useAnonymousPrequalDraftAdapter` for the public
 * funnel, and by the assisted surface for session-authenticated completion.
 */
export interface PrequalDraftAdapter {
  readonly mode: PrequalPersistenceMode;
  readonly role: PrequalRole;

  /**
   * Rehydrate an in-progress application, or null when there is nothing to
   * resume.
   *
   * A READ, and it must stay one. The invited resume address is the invite
   * address, which every corporate mail scanner, link-preview bot, security
   * appliance and browser prefetch fetches unattended — so this creates
   * nothing, and it does not bump last-activity either.
   */
  load(): Promise<PrequalDraftLoad | null>;

  /** Persist the visitor's progress. The adapter decides whether that means
   *  minting the row or updating it. */
  persistStep(snapshot: PrequalDraftSnapshot): Promise<PrequalDraftWriteResult>;

  /**
   * Submit. Takes the request the form already builds, so the two contexts
   * share one builder; the adapter routes it to whichever endpoint its
   * credential can reach, and the assisted implementation additionally carries
   * its own completion mode, certifier and suppression — none of which the
   * shared form knows about or should.
   */
  finalize(request: PrequalFinalizeRequest): Promise<PrequalFinalizeResult>;
}

/** Props every funnel page accepts so the assisted surface can drive it. When
 *  absent the page builds its own anonymous adapter from the URL. */
export interface PrequalFunnelPageProps {
  adapter?: PrequalDraftAdapter;
  /**
   * Rendered on the FINALIZE step, just above submit.
   *
   * The seam that lets the assisted surface put its certification controls next
   * to the button they describe WITHOUT forking the form. The public funnel
   * passes nothing, so an anonymous applicant's finalize step is byte-identical
   * — which an `if (assisted)` branch inside the shared component could never
   * promise, and which is the whole point of decision 18.
   */
  finalizeSlot?: ReactNode;
  // WHICH SITE this application belongs to is deliberately NOT a prop here.
  //
  // It used to be, because the assisted surfaces are addressed by draft id and
  // carry no `:slug` for the funnel to read. Threading it through the form made
  // branding something each surface had to opt into, and the surface that
  // forgot — the result screen — rendered Phoenix at the end of a branded
  // application, to that broker's client, sitting next to them.
  //
  // The site is now given to `PrequalBrandProvider`, which resolves it once for
  // everything beneath it. A funnel page just asks `usePartnerBrand()` for
  // whatever its context resolved, and cannot be the thing that forgets.
}

/** The organic create precondition. Checked before the call rather than after
 *  the 400, so an unfinished identity is a quiet no-op instead of an error
 *  message on a funnel the visitor has not finished with yet. */
export function isIdentityComplete(identity: PrequalDraftIdentity): boolean {
  return (
    identity.firstName.trim() !== "" &&
    identity.lastName.trim() !== "" &&
    /\S+@\S+\.\S+/.test(identity.email.trim())
  );
}
